import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { getUserFullName } from "../../helpers/userDisplay"

const getProjectCode = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "—"

const formatDate = value => {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const getDaysLeft = endDate => {
  if (!endDate) return null
  const end = new Date(endDate)
  if (Number.isNaN(end.getTime())) return null
  return Math.max(0, Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24)))
}

const formatHours = project => {
  const owner =
    (project.members || []).find(member => member.role === "owner") ||
    (project.members || [])[0]
  const rawHours = Number(
    project.total_estimated_hours ??
      project.hours_per_week ??
      owner?.estimated_hours ??
      project.total_hours_logged,
  )
  if (!Number.isFinite(rawHours)) return "—"
  const cadence = owner?.hours_cadence || project.hours_cadence || null
  const hours =
    Number.isInteger(rawHours) || rawHours % 1 === 0
      ? Math.round(rawHours)
      : Number(rawHours.toFixed(1))
  if (!cadence) return `${hours}hrs`
  const unit = cadence === "per_day" || cadence === "day" ? "Day" : "Week"
  return `${hours}hrs/${unit}`
}

const formatDateRange = (project, status) => {
  const start = formatDate(project.start_date)
  const end = formatDate(project.end_date)
  if (status === "future") return `Starting: ${start}`
  if (status === "present") {
    return `${start} - ${project.end_date ? end : "Present"}`
  }
  return `${start} - ${end}`
}

const collaboratorDisplayName = person => {
  const user = person?.user || person || {}
  return getUserFullName(user, {
    fallback: getUserFullName(person, { fallback: "Collaborator" }),
  })
}

const mapCollaborators = project => {
  const list =
    project.latest_3_active_collabs ||
    project.latest_active_collabs ||
    project.members ||
    []

  return list.map(member => {
    const user = member.user || member
    return {
      name: collaboratorDisplayName(member),
      src: getUserAvatarUrl(user),
    }
  })
}

export const mapProfileProject = (project, status) => {
  const category =
    project.categories?.[0]?.name ||
    (typeof project.categories?.[0] === "string"
      ? project.categories[0]
      : null) ||
    "—"
  const role =
    project.role_types?.[0]?.public_name ||
    project.role_types?.[0]?.name ||
    "—"
  const daysLeft =
    status === "present"
      ? project.days_left != null
        ? Number(project.days_left)
        : getDaysLeft(project.end_date)
      : null
  const collaborators = mapCollaborators(project)
  const totalCollaborators = Number(
    project.total_active_collabs ?? collaborators.length,
  )
  const estimatedHours = Number(project.total_estimated_hours)
  const hoursLogged = Number(project.total_hours_logged)
  const safeEstimated = Number.isFinite(estimatedHours)
    ? Math.max(0, estimatedHours)
    : 0
  const safeLogged = Number.isFinite(hoursLogged) ? Math.max(0, hoursLogged) : 0
  // (total_hours_logged / total_estimated_hours) * 100 → 2 decimal places
  const hoursProgressPercent =
    safeEstimated > 0
      ? Math.min(
          100,
          Math.max(
            0,
            Math.round((safeLogged / safeEstimated) * 10000) / 100,
          ),
        )
      : 0

  return {
    id: project.id,
    code: getProjectCode(project.name),
    title: project.name || "—",
    role,
    date: formatDateRange(project, status),
    hrs: formatHours(project),
    genre: category,
    collaborators,
    totalCollaborators: Number.isFinite(totalCollaborators)
      ? Math.max(0, totalCollaborators)
      : collaborators.length,
    completed: status === "past",
    status:
      status === "past"
        ? "Completed"
        : status === "future"
          ? "Starting From"
          : "In Progress",
    statusDate:
      status === "past"
        ? formatDate(project.end_date)
        : status === "future"
          ? formatDate(project.start_date)
          : null,
    daysLeft: Number.isFinite(daysLeft) ? daysLeft : null,
    // Hours-based % for every project payload (2 decimal places).
    progress: hoursProgressPercent,
    estimatedHours: safeEstimated,
    hoursLogged: safeLogged,
  }
}

export const PROFILE_PROJECT_STATUSES = ["past", "present", "future"]

const getMemberUserId = member =>
  member?.user_id || member?.user?.id || null

const extractProjectsFromResponse = response => {
  if (!response?.data?.success) return []
  const result = response.data.result || {}
  return Array.isArray(result) ? result : result.data || []
}

/**
 * Whether `userId` appears on the project payload this endpoint returns
 * (`latest_3_active_collabs`, optional members, role_types pivot).
 * Preview lists can be incomplete when total_active_collabs > 3.
 */
export const projectIncludesCollaborator = (project, userId) => {
  if (!userId || !project) return false
  const target = String(userId)

  const ownerId =
    project.user_id ||
    project.owner_id ||
    project.created_by ||
    project.user?.id ||
    null
  if (ownerId && String(ownerId) === target) return true

  const membershipList = [
    ...(Array.isArray(project.members) ? project.members : []),
    ...(Array.isArray(project.collaborators) ? project.collaborators : []),
    ...(Array.isArray(project.latest_3_active_collabs)
      ? project.latest_3_active_collabs
      : []),
    ...(Array.isArray(project.latest_active_collabs)
      ? project.latest_active_collabs
      : []),
  ]

  if (
    membershipList.some(member => {
      const memberId = getMemberUserId(member)
      return memberId != null && String(memberId) === target
    })
  ) {
    return true
  }

  const roleTypes = Array.isArray(project.role_types) ? project.role_types : []
  return roleTypes.some(roleType => {
    const pivotUserId = roleType?.pivot?.user_id
    return pivotUserId != null && String(pivotUserId) === target
  })
}

/**
 * Project IDs the viewer belongs to for a given status.
 * Same endpoint shape as present projects (no unsupported `members` with[]).
 */
const fetchViewerProjectIdSet = async (viewerUserId, status) => {
  const ids = new Set()
  if (!viewerUserId || !status) return ids

  try {
    let page = 1
    let lastPage = 1

    do {
      const response = await get(endpoints.getUserProjects(viewerUserId), {
        params: {
          status,
          page,
          page_size: 100,
        },
        skipErrorToast: true,
      })
      const result = response?.data?.result || {}
      const list = extractProjectsFromResponse(response)
      list.forEach(project => {
        if (project?.id != null) ids.add(String(project.id))
      })
      lastPage = Math.max(1, Number(result.last_page) || 1)
      page += 1
    } while (page <= lastPage && page <= 20)
  } catch {
    // Leave set empty — fail closed (hide future projects).
  }

  return ids
}

export const fetchProfileProjects = async ({
  userId,
  status,
  viewerUserId = null,
  collaboratorOnly = false,
} = {}) => {
  if (!userId || !status) return []

  // Future projects on public profiles are collaborator-only: guests see none.
  if (collaboratorOnly && !viewerUserId) return []

  try {
    // Match present/past: status only — this endpoint does not support with[]=members.
    const response = await get(endpoints.getUserProjects(userId), {
      params: { status },
    })
    if (!response?.data?.success) return []

    let list = extractProjectsFromResponse(response)

    if (collaboratorOnly && String(viewerUserId) !== String(userId)) {
      // Intersect with the viewer's own projects of the same status (reliable
      // membership). Also keep latest_3 / pivot matches as a soft fallback.
      const viewerProjectIds = await fetchViewerProjectIdSet(
        viewerUserId,
        status,
      )
      list = list.filter(project => {
        const projectId =
          project?.id != null ? String(project.id) : null
        if (projectId && viewerProjectIds.has(projectId)) return true
        return projectIncludesCollaborator(project, viewerUserId)
      })
    }

    return list.map(project => mapProfileProject(project, status))
  } catch {
    return []
  }
}

/**
 * @param {object} options
 * @param {string|number} options.userId — profile being viewed
 * @param {string|number|null} [options.viewerUserId] — logged-in viewer
 * @param {boolean} [options.restrictFutureToViewerCollaborator]
 *   When true (public profile), only return future projects the viewer
 *   collaborates on. Past / present stay unrestricted.
 */
export const fetchProfileProjectSections = async ({
  userId,
  viewerUserId = null,
  restrictFutureToViewerCollaborator = false,
} = {}) => {
  const empty = { past: [], present: [], future: [] }
  if (!userId) return empty

  const [past, present, future] = await Promise.all(
    PROFILE_PROJECT_STATUSES.map(status =>
      fetchProfileProjects({
        userId,
        status,
        viewerUserId,
        collaboratorOnly:
          restrictFutureToViewerCollaborator && status === "future",
      }),
    ),
  )

  return { past, present, future }
}

/**
 * Human duration between role start and end (or now if ongoing).
 * Always shows a value when the range is valid — including short spans
 * under one month (days), which previously rendered as "—".
 */
const formatDuration = (startDate, endDate, isOngoing) => {
  if (!startDate) return "—"
  const start = new Date(startDate)
  if (Number.isNaN(start.getTime())) return "—"
  const end = isOngoing || !endDate ? new Date() : new Date(endDate)
  if (Number.isNaN(end.getTime()) || end < start) return "—"

  const totalDays = Math.floor((end - start) / (1000 * 60 * 60 * 24))
  if (totalDays < 1) return "< 1 day"

  const years = Math.floor(totalDays / 365)
  if (years >= 1) {
    const remDays = totalDays - years * 365
    const months = Math.floor(remDays / 30)
    const yearLabel = `${years} ${years === 1 ? "year" : "years"}`
    if (months > 0) {
      return `${yearLabel} ${months} ${months === 1 ? "month" : "months"}`
    }
    return yearLabel
  }

  const months = Math.floor(totalDays / 30)
  if (months >= 1) {
    return `${months} ${months === 1 ? "month" : "months"}`
  }

  return `${totalDays} ${totalDays === 1 ? "day" : "days"}`
}

export const mapOngoingRole = role => {
  const roleType =
    role?.role_types?.[0] || role?.roleTypes?.[0] || role?.role_type || {}
  const isOngoing = Boolean(role?.is_ongoing) || !role?.end_date
  const start = formatDate(role?.start_date)
  const end = isOngoing ? "Ongoing" : formatDate(role?.end_date)
  const roleName =
    (typeof role?.role === "string" && role.role.trim()) ||
    roleType.public_name ||
    roleType.name ||
    "—"

  const categories = Array.isArray(role?.categories)
    ? role.categories
    : role?.category
      ? [role.category]
      : []
  const genres = categories
    .map(item =>
      typeof item === "string" ? item.trim() : String(item?.name || "").trim(),
    )
    .filter(Boolean)

  return {
    id: role?.id,
    code: getProjectCode(role?.project_name),
    title: role?.project_name || "—",
    genre: genres[0] || "—",
    genres: genres.length > 0 ? genres : ["—"],
    role: roleName,
    date: `${start} – ${end}`,
    duration: formatDuration(role?.start_date, role?.end_date, isOngoing),
  }
}

export const fetchOngoingRoles = async () => {
  const response = await get(endpoints.onGoingRole, {
    params: {
      page: 1,
      page_size: 100,
      "with[]": ["roleTypes", "categories"],
    },
    skipErrorToast: true,
  })
  if (!response?.data?.success) return []
  const result = response.data.result || {}
  const list = Array.isArray(result) ? result : result.data || []
  return list.map(mapOngoingRole)
}

/**
 * GET /api/v1/on-going-roles?id=:roleId
 * Backend may still return a paginated list — pick the matching row by id.
 */
export const fetchOngoingRoleById = async roleId => {
  if (!roleId) return null

  try {
    const response = await get(endpoints.onGoingRole, {
      params: {
        id: roleId,
        page: 1,
        page_size: 100,
        "with[]": ["roleTypes", "categories", "cities"],
      },
      skipErrorToast: true,
    })
    if (!response?.data?.success) return null

    const result = response.data.result
    const list = Array.isArray(result)
      ? result
      : Array.isArray(result?.data)
        ? result.data
        : result?.id
          ? [result]
          : []

    return (
      list.find(item => String(item?.id) === String(roleId)) || null
    )
  } catch {
    return null
  }
}
